import React, { useState } from 'react';
import { Chrome, Moon, Sun } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { GamutLogo } from '@/components/ui/GamutLogo';
import { AppleIcon } from '@/components/ui/AppleIcon';
import { federatedProvidersFor } from '@/lib/federatedProviders';
import { firebaseConfig } from '@/lib/firebase';
import { useTheme } from '@/contexts/ThemeContext';
import { THEME_LOCKED_FOR_PILOT } from '@/config/pilot';
import { useResolvedTheme } from '@/hooks/useResolvedTheme';
import { auth } from '@/lib/firebase';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendEmailVerification,
    fetchSignInMethodsForEmail,
} from 'firebase/auth';
import { getFirebaseAuthErrorCode, getUserFriendlyMessage } from '@/utils/errorMessages';
import { provisionOwnerUnlessInvited } from '@/lib/provisionNewOwnerUser';
import {
    buildPasswordFailureHintMessage,
    completeOAuthOwnerSignIn,
    signInWithApplePopup,
    signInWithGooglePopup,
} from '@/lib/socialSignIn';
import { logger } from '@/lib/logger';

export const LoginPage: React.FC = () => {
    const { theme, setTheme } = useTheme();
    const resolvedTheme = useResolvedTheme();
    const [isRegister, setIsRegister] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [registerNotice, setRegisterNotice] = useState('');
    const navigate = useNavigate();

    // WEB-67: an invitee who signs up here instead of following their invite
    // goes into that invite, never into a new owner org of their own.
    const goToInvite = (token: string) => navigate(`/join?token=${encodeURIComponent(token)}`, { replace: true });

    // WEB-02: which providers this build may offer, derived from the Firebase
    // project it points at. See src/lib/federatedProviders.ts - gamut-prod has
    // none configured, so production renders no social buttons at all.
    const federatedProviders = federatedProvidersFor(firebaseConfig.projectId);

    const handleGoogleSignIn = async () => {
        setLoading(true);
        setError('');
        setRegisterNotice('');
        try {
            const credential = await signInWithGooglePopup();
            const joinToken = await completeOAuthOwnerSignIn(credential);
            if (joinToken) goToInvite(joinToken);
        } catch (err: unknown) {
            logger.error('Google auth error:', err);
            setError(getUserFriendlyMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const handleAppleSignIn = async () => {
        setLoading(true);
        setError('');
        setRegisterNotice('');
        try {
            const credential = await signInWithApplePopup();
            const joinToken = await completeOAuthOwnerSignIn(credential);
            if (joinToken) goToInvite(joinToken);
        } catch (err: unknown) {
            logger.error('Apple auth error:', err);
            setError(getUserFriendlyMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setRegisterNotice('');

        try {
            if (isRegister) {
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const { user } = userCredential;

                try {
                    await sendEmailVerification(user);
                } catch (verifyErr) {
                    logger.error('sendEmailVerification:', verifyErr);
                }

                const joinToken = await provisionOwnerUnlessInvited(user, name);

                await user.getIdToken(true);

                if (joinToken) {
                    goToInvite(joinToken);
                    return;
                }

                setRegisterNotice('Check your email to verify your address. You can continue using the app while signed in.');
            } else {
                const credential = await signInWithEmailAndPassword(auth, email, password);
                await credential.user.getIdToken(true);
            }
        } catch (err: unknown) {
            logger.error('Auth error:', err);
            let message = getUserFriendlyMessage(err);
            if (!isRegister && email.trim()) {
                const code = getFirebaseAuthErrorCode(err) ?? '';
                if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') {
                    try {
                        const methods = await fetchSignInMethodsForEmail(auth, email.trim());
                        message = buildPasswordFailureHintMessage(message, methods, federatedProviders);
                    } catch {
                        message = buildPasswordFailureHintMessage(message, [], federatedProviders);
                    }
                }
            }
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const cycleTheme = () => {
        if (theme === 'dark' || (theme === 'system' && resolvedTheme === 'dark')) {
            setTheme('light');
        } else if (theme === 'light' || (theme === 'system' && resolvedTheme === 'light')) {
            setTheme('soft-light');
        } else {
            setTheme('dark');
        }
    };

    const themeLabel =
        theme === 'light'
            ? 'Light Mode'
            : theme === 'soft-light'
                ? 'Soft Light Mode'
                : theme === 'system'
                    ? `System (${resolvedTheme === 'dark' ? 'Dark' : 'Light'})`
                    : 'Dark Mode';

    const themeIcon =
        theme === 'light' ? (
            <Sun size={18} />
        ) : theme === 'soft-light' ? (
            <Sun size={18} className="opacity-70" />
        ) : theme === 'system' ? (
            resolvedTheme === 'dark' ? <Moon size={18} /> : <Sun size={18} className="opacity-90" />
        ) : (
            <Moon size={18} />
        );

    const toggleLabel = `Current: ${themeLabel} (Click to cycle)`;

    return (
        <div className="relative h-screen flex items-center justify-center bg-bg-primary mesh-gradient p-6">
            {!THEME_LOCKED_FOR_PILOT && (
                <button
                    type="button"
                    onClick={cycleTheme}
                    className="absolute top-6 right-6 flex items-center justify-center p-3 rounded-xl transition-all duration-200 text-text-secondary hover:bg-surface-hover hover:text-text-primary border border-divider-subtle bg-bg-primary/20 cursor-pointer"
                    title={toggleLabel}
                    aria-label={toggleLabel}
                >
                    <span className="text-accent-electric">{themeIcon}</span>
                </button>
            )}
            <div className="glass p-10 w-full max-w-md border border-divider-subtle shadow-2xl">
                <GamutLogo alt="Gamut Logo" className="mx-auto mb-8 h-24 object-contain drop-shadow-[0_0_15px_rgb(var(--accent-primary-rgb)/0.5)]" />
                <p className="text-center text-text-secondary mb-10 text-sm font-medium tracking-wide">
                    Restoration Management Reimagined
                </p>

                {error && <div className="text-red-500 mb-4 text-xs font-bold uppercase tracking-wider bg-red-500/10 p-3 rounded-lg border border-red-500/20">{error}</div>}
                {registerNotice && <div className="text-accent-electric mb-4 text-xs font-bold uppercase tracking-wider bg-accent-electric/10 p-3 rounded-lg border border-accent-electric/20">{registerNotice}</div>}

                <form onSubmit={handleSubmit} className="space-y-5">
                    {isRegister && (
                        <div>
                            <label className="block mb-2 text-xs font-bold uppercase tracking-widest text-text-muted">Full Name</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                autoComplete="name"
                                className="w-full p-3.5 rounded-xl bg-surface-hover border border-divider-subtle text-text-primary focus:border-accent-electric focus:outline-none transition-all placeholder:text-text-muted"
                                placeholder="Enter your full name"
                            />
                        </div>
                    )}
                    <div>
                        <label className="block mb-2 text-xs font-bold uppercase tracking-widest text-text-muted">Email Address</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="email"
                            className="w-full p-3.5 rounded-xl bg-surface-hover border border-divider-subtle text-text-primary focus:border-accent-electric focus:outline-none transition-all placeholder:text-text-muted"
                            placeholder="name@company.com"
                        />
                    </div>
                    <div>
                        <label className="block mb-2 text-xs font-bold uppercase tracking-widest text-text-muted">Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            autoComplete={isRegister ? "new-password" : "current-password"}
                            className="w-full p-3.5 rounded-xl bg-surface-hover border border-divider-subtle text-text-primary focus:border-accent-electric focus:outline-none transition-all placeholder:text-text-muted"
                            placeholder="••••••••"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-4 rounded-xl bg-linear-to-br from-accent-primary to-accent-secondary text-text-primary font-bold shadow-[0_0_20px_rgb(var(--accent-primary-rgb)/0.3)] hover:shadow-[0_0_30px_rgb(var(--accent-primary-rgb)/0.5)] transition-all transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 mt-4 cursor-pointer"
                    >
                        {loading ? 'Processing...' : isRegister ? 'Create Account' : 'Sign In'}
                    </button>

                    <p className="text-center text-sm text-text-muted pt-2">
                        {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={() => setIsRegister(!isRegister)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setIsRegister(!isRegister);
                                }
                            }}
                            className="text-accent-electric cursor-pointer font-bold hover:underline"
                        >
                            {isRegister ? 'Sign In' : 'Create One'}
                        </span>
                    </p>
                </form>

                {/* WEB-02: only render providers that are actually configured for this
                    Firebase project. gamut-prod has none, so in production this whole
                    block - divider included - is absent rather than showing buttons
                    whose only possible outcome is a popup error. */}
                {federatedProviders.length > 0 && (
                <div className="relative my-8">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-divider-subtle" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase tracking-widest">
                        <span className="bg-transparent px-3 text-text-muted">Or</span>
                    </div>
                </div>
                )}

                <div className="space-y-3">
                    {federatedProviders.includes('google') && (
                    <button
                        type="button"
                        onClick={handleGoogleSignIn}
                        disabled={loading}
                        className="w-full py-4 rounded-xl border border-divider-subtle bg-surface-hover text-text-primary font-bold flex items-center justify-center gap-2 hover:bg-surface-active hover:border-divider-subtle transition-all disabled:opacity-50 cursor-pointer"
                    >
                        <Chrome className="h-5 w-5" aria-hidden />
                        Continue with Google
                    </button>
                    )}

                    {federatedProviders.includes('apple') && (
                    <button
                        type="button"
                        onClick={handleAppleSignIn}
                        disabled={loading}
                        className="w-full py-4 rounded-xl bg-black text-white font-bold flex items-center justify-center gap-2 hover:bg-neutral-900 transition-all disabled:opacity-50 cursor-pointer border border-black"
                    >
                        <AppleIcon className="h-5 w-5" />
                        Continue with Apple
                    </button>
                    )}
                </div>

                <div className="mt-8 pt-6 border-t border-divider-subtle text-center">
                    <Link to="/privacy" className="text-xs text-text-muted hover:text-accent-electric transition-colors">
                        Privacy Policy
                    </Link>
                </div>

                <p className="text-center text-xs text-text-muted mt-3">
                    This will create an account if you don&apos;t have one.
                </p>
            </div>
        </div>
    );
};
